import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { GlobalConfigService } from './global-config.service';

/**
 * Params aceitos pela criação de QR de upload de fotos de produto.
 */
export interface CreateProductPhotosQrParams {
  treatmentPlanId: string;
  productId: string;
  photoType?: string;
  modules?: string;        // base64 JSON
  moduleId?: string;
  clinicId?: string;
}

/**
 * Params aceitos pela criação de QR de upload de fotos de tratamento.
 */
export interface CreateTreatmentPhotosQrParams {
  treatmentPlanId: string;
  photoType?: string;
  modules?: string;
  moduleId?: string;
  clinicId?: string;
}

export interface CreateQrResponse {
  token: string;
  expiresAt: string;
}

export interface ResolveQrResponse {
  params: Record<string, any>;
  hmacToken: string;
  expiresAt: string;
}

/**
 * Cliente do endpoint /qr do backend. Substitui o esquema antigo de embutir
 * JWT do criador na URL do QR — esse esquema gerava URLs longas (~1.500 chars)
 * que o WhatsApp truncava ao reconhecer como link clicável.
 *
 * Fluxo:
 *   1) Atendente loga no painel e clica "gerar QR"
 *      → frontend chama createProductPhotos(...) ou createTreatmentPhotos(...)
 *      → backend persiste params em qr_links e retorna token de ~22 chars
 *   2) URL do QR fica https://westhetics.com.br/upload-photos?qr=<token>
 *   3) Paciente abre no celular
 *      → frontend lê params['qr'], chama resolveProductPhotos(token)
 *      → backend retorna params + HMAC token de 30 min
 *   4) Frontend usa o HMAC pros uploads
 */
@Injectable({
  providedIn: 'root'
})
export class QrLinkService {

  private get baseUrl(): string {
    return this.globalConfig.getApiUrl() + '/qr';
  }

  constructor(
    private http: HttpClient,
    private globalConfig: GlobalConfigService
  ) { }

  // ─────────────────────────────────────────────────────────────────────
  //  Criação (autenticada — atendente logada)
  // ─────────────────────────────────────────────────────────────────────

  createProductPhotos(params: CreateProductPhotosQrParams): Observable<CreateQrResponse> {
    return this.http.post<CreateQrResponse>(`${this.baseUrl}/product-photos`, params);
  }

  createTreatmentPhotos(params: CreateTreatmentPhotosQrParams): Observable<CreateQrResponse> {
    return this.http.post<CreateQrResponse>(`${this.baseUrl}/treatment-photos`, params);
  }

  // ─────────────────────────────────────────────────────────────────────
  //  Resolução (pública — paciente sem sessão)
  // ─────────────────────────────────────────────────────────────────────

  resolveProductPhotos(token: string): Observable<ResolveQrResponse> {
    return this.http.get<ResolveQrResponse>(`${this.baseUrl}/public/product-photos/${encodeURIComponent(token)}`);
  }

  resolveTreatmentPhotos(token: string): Observable<ResolveQrResponse> {
    return this.http.get<ResolveQrResponse>(`${this.baseUrl}/public/treatment-photos/${encodeURIComponent(token)}`);
  }
}
